import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '@/App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Building2, FileSpreadsheet, Upload, RefreshCw } from 'lucide-react';

export default function SuperAdminDashboard() {
    const { API, user } = useContext(AuthContext);
    const [companies, setCompanies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [creating, setCreating] = useState(false);
    const [coaCounts, setCoaCounts] = useState({}); // company_id -> count
    const [uploadingCoa, setUploadingCoa] = useState(null); // company_id being uploaded

    const [newCompany, setNewCompany] = useState({
        company_name: '',
        company_slug: '',
        company_rut: '',
        user_name: '',
        user_email: '',
        user_password: ''
    });

    useEffect(() => {
        fetchCompanies();
    }, []);

    const fetchCompanies = async () => {
        try {
            const response = await axios.get(`${API}/admin/companies`);
            const companyList = Array.isArray(response.data) ? response.data : [];
            setCompanies(companyList);
            fetchCoaCounts(companyList);
        } catch (error) {
            console.error(error);
            toast.error('Error al cargar empresas');
        } finally {
            setLoading(false);
        }
    };

    const fetchCoaCounts = async (companyList) => {
        const counts = {};
        for (const company of companyList) {
            try {
                const res = await axios.get(`${API}/admin/companies/${company.id}/chart-of-accounts`);
                counts[company.id] = res.data.count || 0;
            } catch {
                counts[company.id] = 0;
            }
        }
        setCoaCounts(counts);
    };

    const loadDefaultCoa = async (companyId) => {
        try {
            const res = await axios.post(`${API}/admin/companies/${companyId}/chart-of-accounts/default`);
            toast.success(`${res.data.count} cuentas cargadas`);
            fetchCoaCounts(companies);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Error al cargar plan de cuentas');
        }
    };

    const handleCoaUpload = async (companyId, file) => {
        setUploadingCoa(companyId);
        try {
            const formData = new FormData();
            formData.append('file', file);
            const res = await axios.post(`${API}/admin/companies/${companyId}/chart-of-accounts/upload`, formData);
            toast.success(`${res.data.count} cuentas importadas`);
            fetchCoaCounts(companies);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Error al importar');
        } finally {
            setUploadingCoa(null);
        }
    };

    const toggleCompanyStatus = async (companyId, currentStatus) => {
        try {
            await axios.put(`${API}/admin/companies/${companyId}/status`, {
                is_active: !currentStatus
            });
            toast.success(`Empresa ${!currentStatus ? 'activada' : 'pausada'}`);
            fetchCompanies();
        } catch (error) {
            toast.error('Error al cambiar estado');
        }
    };

    const handleCreateCompany = async (e) => {
        e.preventDefault();
        setCreating(true);
        try {
            await axios.post(`${API}/admin/companies`, newCompany);
            toast.success('Empresa creada exitosamente');
            setIsCreateModalOpen(false);
            setNewCompany({
                company_name: '',
                company_slug: '',
                company_rut: '',
                user_name: '',
                user_email: '',
                user_password: ''
            });
            fetchCompanies();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Error al crear empresa');
        } finally {
            setCreating(false);
        }
    };

    if (loading) return <div className="p-8">Cargando panel de administración...</div>;

    return (
        <div className="p-8 space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="text-3xl font-bold tracking-tight">Panel Super Admin</h2>
                    <p className="text-muted-foreground">
                        Gestión global de empresas y usuarios ·
                        <a href="/plantilla_plan_cuentas.csv" download className="text-primary hover:underline ml-1">
                            Descargar plantilla CSV Plan de Cuentas
                        </a>
                    </p>
                </div>
                <Dialog open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen}>
                    <DialogTrigger asChild>
                        <Button className="bg-primary">
                            <Plus className="h-4 w-4 mr-2" />
                            Nueva Empresa
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-[500px]">
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2">
                                <Building2 className="h-5 w-5" />
                                Crear Nueva Empresa
                            </DialogTitle>
                        </DialogHeader>
                        <form onSubmit={handleCreateCompany} className="space-y-4 py-4">
                            <div className="space-y-4">
                                <h4 className="font-medium text-sm text-muted-foreground">Datos de la Empresa</h4>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label>Nombre Empresa</Label>
                                        <Input
                                            value={newCompany.company_name}
                                            onChange={(e) => setNewCompany({ ...newCompany, company_name: e.target.value })}
                                            placeholder="Mi Empresa Ltda"
                                            required
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>RUT Empresa</Label>
                                        <Input
                                            value={newCompany.company_rut}
                                            onChange={(e) => {
                                                let val = e.target.value.replace(/[^0-9kK]/g, '');
                                                if (val.length > 1) {
                                                    const dv = val.slice(-1);
                                                    const body = val.slice(0, -1);
                                                    val = body.replace(/\B(?=(\d{3})+(?!\d))/g, ".") + "-" + dv;
                                                }
                                                setNewCompany({ ...newCompany, company_rut: val });
                                            }}
                                            placeholder="76.415.653-6"
                                            required
                                        />
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Label>ID de Acceso (Slug)</Label>
                                    <Input
                                        value={newCompany.company_slug}
                                        onChange={(e) => setNewCompany({ ...newCompany, company_slug: e.target.value.toLowerCase().replace(/\s+/g, '') })}
                                        placeholder="miempresa"
                                        required
                                    />
                                    <p className="text-xs text-muted-foreground">Este ID se usará para iniciar sesión.</p>
                                </div>
                            </div>

                            <div className="space-y-4 pt-4 border-t">
                                <h4 className="font-medium text-sm text-muted-foreground">Usuario Administrador</h4>
                                <div className="space-y-2">
                                    <Label>Nombre Administrador</Label>
                                    <Input
                                        value={newCompany.user_name}
                                        onChange={(e) => setNewCompany({ ...newCompany, user_name: e.target.value })}
                                        placeholder="Juan Pérez"
                                        required
                                    />
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label>Email</Label>
                                        <Input
                                            type="email"
                                            value={newCompany.user_email}
                                            onChange={(e) => setNewCompany({ ...newCompany, user_email: e.target.value })}
                                            placeholder="admin@empresa.cl"
                                            required
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Contraseña</Label>
                                        <Input
                                            type="password"
                                            value={newCompany.user_password}
                                            onChange={(e) => setNewCompany({ ...newCompany, user_password: e.target.value })}
                                            placeholder="••••••••"
                                            required
                                        />
                                    </div>
                                </div>
                            </div>

                            <DialogFooter>
                                <Button type="button" variant="outline" onClick={() => setIsCreateModalOpen(false)}>
                                    Cancelar
                                </Button>
                                <Button type="submit" disabled={creating}>
                                    {creating ? 'Creando...' : 'Crear Empresa'}
                                </Button>
                            </DialogFooter>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>

            <Card>
                <CardHeader>
                    <CardTitle>Empresas Registradas</CardTitle>
                    <CardDescription>Listado y control de estados</CardDescription>
                </CardHeader>
                <CardContent>
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Nombre</TableHead>
                                <TableHead>Slug</TableHead>
                                <TableHead>RUT</TableHead>
                                <TableHead>Fecha Creación</TableHead>
                                <TableHead>Plan de Cuentas</TableHead>
                                <TableHead>Estado</TableHead>
                                <TableHead>Acciones</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {Array.isArray(companies) && companies.map((company) => (
                                <TableRow key={company.id}>
                                    <TableCell className="font-medium">{company.name}</TableCell>
                                    <TableCell>
                                        <Badge variant="outline">{company.slug}</Badge>
                                    </TableCell>
                                    <TableCell>{company.rut}</TableCell>
                                    <TableCell>{new Date(company.created_at).toLocaleDateString()}</TableCell>
                                    <TableCell>
                                        <div className="flex items-center gap-2">
                                            <Badge variant={coaCounts[company.id] > 0 ? "default" : "secondary"}>
                                                {coaCounts[company.id] || 0} cuentas
                                            </Badge>
                                            {coaCounts[company.id] < 50 ? (
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={() => loadDefaultCoa(company.id)}
                                                    className="h-7 text-xs"
                                                >
                                                    <RefreshCw className="h-3 w-3 mr-1" />
                                                    Cargar Default
                                                </Button>
                                            ) : null}
                                            <label className="cursor-pointer">
                                                <input
                                                    type="file"
                                                    accept=".csv"
                                                    className="hidden"
                                                    onChange={(e) => e.target.files[0] && handleCoaUpload(company.id, e.target.files[0])}
                                                    disabled={uploadingCoa === company.id}
                                                />
                                                <Button size="sm" variant="ghost" asChild className="h-7 text-xs">
                                                    <span>
                                                        <Upload className="h-3 w-3 mr-1" />
                                                        {uploadingCoa === company.id ? 'Subiendo...' : 'CSV'}
                                                    </span>
                                                </Button>
                                            </label>
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        <Badge variant={company.is_active ? "default" : "destructive"}>
                                            {company.is_active ? 'Activo' : 'Pausado'}
                                        </Badge>
                                    </TableCell>
                                    <TableCell>
                                        <div className="flex items-center space-x-2">
                                            <Switch
                                                checked={company.is_active}
                                                onCheckedChange={() => toggleCompanyStatus(company.id, company.is_active)}
                                            />
                                            <span className="text-sm text-muted-foreground">
                                                {company.is_active ? 'Desactivar' : 'Activar'}
                                            </span>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>
        </div>
    );
}
